import { useRef, useState } from "react";
import { ipaMulti, ipaSingle } from "./ipaAlts";

/**
 * A text box that makes it easier to enter IPA.
 * If you hold the shift key and tap any letter or some symbols,
 * it will cycle between different possible IPA.
 * It will also make some obvious substitutions, like primary
 * stress for ' and long for :
 * 
 * You can insert text at the current cursor position
 *  by passing the string in an array, e.g., ['text to insert'],
 *  which is a hacky way for the control to know that you
 *  want to insert something new (because the pointer has changed).
 * 
 * Optionally, you can pass custom sets of keys rather than general IPA
 */
export function IpaKeyboard(props: {
  value: string,
  onChange: (s: string) => void;
  cols?: number,
  rows?: number,
  id?: string,
  /** insert given string at current cursor position (each array instance should be unique) */
  insert?: [string],
  /** set to true to use <input>, can be useful since <textarea>'s baseline is undefined */
  useInput?: boolean,
  onKeyDown?: (key: string) => void,
  /** control will call this when ctrl-s is typed */
  onSpeak?: () => void,

  /** map from typed-key to replacement symbol */
  single?: Record<string, string>,
  /** map from shift-key (like S) to a list of options to cycle between */
  multi?: Record<string, string[]>,
}) {
  const [widget, setWidget] = useState<HTMLTextAreaElement | HTMLInputElement>();
  const [lastValue, setLastValue] = useState<string>(props.value);
  const [delayedCheck, setDelayedCheck] = useState(false);
  const [lastInsert, setLastInsert] = useState(['']);
  const [shiftCount, setShiftCount] = useState<number | null>(null);
  const [shiftedKey, setShiftedKey] = useState('');
  const [suppress, setSuppress] = useState(false);  /** don't send a change event */
  const refTA = useRef<HTMLTextAreaElement>(null);
  const refIn = useRef<HTMLInputElement>(null);
  const ref = refTA.current !== null ? refTA : refIn;
  if (widget === undefined && ref && ref.current) {
    setWidget(ref.current);
  }
  const singleMap = props.single ?? ipaSingle;
  const multiMap = props.multi ?? ipaMulti;

  // has parent changed the text?
  if (props.value !== lastValue) {
    // if different, wait a bit for events to finish percolating
    // before checking again to see if the parent asked for a change
    setTimeout(() => setDelayedCheck(true), 10/*ms*/); // 0ms didn't work - this feels fragile
  }
  if (delayedCheck) {  // delayed check
    setDelayedCheck(false);
    if (props.value !== lastValue && widget) {
      // if it's still different, the parent must have changed it
      widget.value = props.value;
      setLastValue(props.value);
    }
  }

  // insert when passed a new object
  if (props.insert !== undefined && props.insert !== lastInsert && widget) {
    insertText(widget, props.insert[0], 0);
    widget.focus({ preventScroll: true });
    setLastInsert(props.insert);
  }

  // tell parent about change, delayed to avoid two components updating at same time
  function sendChange(w: HTMLTextAreaElement | HTMLInputElement) {
    if (w) {
      setLastValue(w.value);
    }
    setTimeout(() => {
      if (w && props.onChange) {
        props.onChange(w.value);
      }
    }, 0/*ms*/);
  }

  // insert text at the current cursor (- at)
  function insertText(w: HTMLTextAreaElement | HTMLInputElement, s: string, at: number) {
    if (w.selectionStart === null || w.selectionEnd === null) {
      return;
    }
    const start = Math.max(w.selectionStart - at, 0);
    w.setRangeText(s, start, w.selectionEnd, "end");
    setSuppress(false);
    sendChange(w);
  }

  // tell parent about text changes unless it's a temporary change that doesn't count
  function onChange(w: HTMLTextAreaElement | HTMLInputElement) {
    if (widget === undefined) {
      setWidget(w);
    }

    if (suppress) {
      setSuppress(false);
    } else {
      sendChange(w);
    }
  }

  // figure out if shift key is being held down as another key is clicked
  function onKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) {
    if (widget === undefined) {
      setWidget(event.target as HTMLTextAreaElement | HTMLInputElement);
    }

    if (props.onSpeak && event.ctrlKey && event.key === 's') {
      event.preventDefault();
      props.onSpeak();  // ctrl-s can trigger speaking
    }

    if (event.shiftKey && event.repeat && multiMap[event.key] !== undefined) {
      // repeat with shift pressed isn't useful so ignore the keystroke
      event.preventDefault();
      return;
    }
    if (props.onKeyDown) {
      props.onKeyDown(event.key);
    }

    if (event.shiftKey && event.key === shiftedKey) {
      // shift still pressed along with same other key
      setShiftCount(shiftCount === null ? 0 : shiftCount + 1);
      if (multiMap[event.key]) {
        setSuppress(true);  // current text (e.g. 'S') will be replaced (e.g. 'ʃ')
      }
    } else if (event.shiftKey && event.key !== 'Shift') {
      // shift pressed with a new key -> reset shift count
      setShiftCount(0);
      setShiftedKey(event.key);
      if (multiMap[event.key]) {
        setSuppress(true);  // current text (e.g. 'S') will be replaced (e.g. 'ʃ')
      }
    } else {
      // anything else -> clear shift info
      setShiftCount(null);
      setShiftedKey('');
      setSuppress(false);
    }
  }

  // possibly replace the key that was just typed with useful IPA
  function onKeyUp(event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) {
    if (event.altKey || event.ctrlKey || event.metaKey) {
      return;
    }

    const w = event.target as HTMLTextAreaElement | HTMLInputElement;

    // check if we should cycle between options
    if (shiftCount !== null && event.key !== 'Shift') {
      const list = multiMap[shiftedKey];
      if (list !== undefined) {
        const lastSymbol = list[(shiftCount - 1) % list.length];
        const lastLen = lastSymbol ? lastSymbol.length : 1;
        const symbol = list[shiftCount % list.length];
        insertText(w, symbol, shiftCount === 0 ? lastLen : lastLen + 1);
        return;
      }
    }

    // check if we should simply replace the typed symbol
    const symbol = singleMap[event.key];
    if (symbol !== undefined) {
      insertText(w, symbol, 1);
    }

    // check if something in multiMap was entered w/out the shift-key
    if (!event.shiftKey && multiMap[event.key]) {
      const symbols = multiMap[event.key];
      if (symbols !== undefined) {
        insertText(w, symbols[0], 1); // simply take the first option
      }
    }
  }

  if (props.useInput) {
    return (
      <input ref={refIn} spellCheck={false} onKeyDown={onKeyDown} onKeyUp={onKeyUp}
        onChange={(e) => onChange(e.target)}
        size={props.cols} defaultValue={props.value} id={props.id} />);
  }
  return (
    <textarea ref={refTA} spellCheck={false} onKeyDown={onKeyDown} onKeyUp={onKeyUp}
      onChange={(e) => onChange(e.target)}
      cols={props.cols} rows={props.rows} defaultValue={props.value} id={props.id} />);
}
